import type { Question } from '@mentimeter/http-clients';
import { memo, useEffect, useState, type JSX } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Bullet } from './Bullet';

export interface StepRenderT {
  isLast: boolean;
  isActive: boolean;
  animationDuration: number;
  length: number;
}

export interface StepComponentT extends StepRenderT {
  id: number;
  label: string;
  imageUrl?: string | undefined;
  fontOffset?: number | undefined;
  isNumbered: boolean;
  number: number;
  letter: string;
  indentations: number;
}

export interface StepT {
  index: number;
  active: boolean;
  last: boolean;
  length: number;
  showAllSteps: boolean;
  enableAnimations: boolean;
  children: (arg0: StepRenderT) => JSX.Element;
}

const Step = memo<StepT>(
  ({
    index,
    active,
    length,
    last,
    showAllSteps,
    enableAnimations,
    children,
  }: StepT) => {
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      if (showAllSteps) {
        if (enableAnimations) {
          timer = setTimeout(
            () => {
              setIsActive(true);
            },
            75 * (index + 1),
          );
        } else {
          setIsActive(true);
        }
      } else if (active) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [active, index, showAllSteps, enableAnimations]);
    return children({
      isLast: last && !showAllSteps,
      isActive,
      animationDuration: enableAnimations ? 500 : 0,
      length,
    });
  },
);

interface StepsT {
  fontOffset?: number;
  enableAnimations: boolean;
  step: number;
  steps: Question['choices'];
  showAllSteps: Question['show_all_bullets'];
  numberedListEnabled: Question['numbered_list'];
}

// Max bullets = 12, so max sub bullets = 11
const LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k'];

export const Steps = ({
  fontOffset,
  enableAnimations,
  step,
  steps,
  showAllSteps,
  numberedListEnabled,
}: StepsT) => {
  let bulletIndex = 1;
  let subBulletIndex = 0;

  return (
    <Box as={numberedListEnabled ? 'ol' : 'ul'}>
      {steps.map((s, i) => {
        const isIndented = s.label.match(/^(-|\*)+\s/g); // Text starts with - or * and a space
        const letter = LETTERS[subBulletIndex] || 'a';
        const number = bulletIndex;
        const label = isIndented ? s.label.substr(1) : s.label;
        const indentations = isIndented ? 1 : 0;
        if (isIndented) {
          subBulletIndex++;
        } else {
          subBulletIndex = 0;
          bulletIndex++;
        }

        return (
          <Step
            key={s.id}
            index={i}
            active={step > i}
            last={i === steps.length - 1}
            length={steps.length}
            showAllSteps={showAllSteps}
            enableAnimations={enableAnimations}
          >
            {(c) => (
              <Bullet
                {...c}
                id={s.id}
                imageUrl={s.cropped_image_url}
                fontOffset={fontOffset}
                isNumbered={numberedListEnabled}
                number={number}
                letter={letter}
                label={label}
                indentations={indentations}
              />
            )}
          </Step>
        );
      })}
    </Box>
  );
};
